import React from "react";
import { FileMap, FileMapField, FileType } from "../insightsProduct";
import axios from "axios";
import _ from "lodash";

type ProgressPercentage = number;

interface FileUploadState {
  name: string;
  progress: ProgressPercentage;
  success: boolean;
  error: boolean;
  done: boolean;
}

export interface UploadProgressState {
  transcript?: FileUploadState;
  abstract?: FileUploadState;
  replay?: FileUploadState;
  eventDeck?: FileUploadState;
}

export type UploadProgressAction =
  | { type: "init"; state: UploadProgressState }
  | { type: "update"; fileType: FileType; progress: number }
  | { type: "done"; fileType: FileType }
  | { type: "error"; fileType: FileType };

const initialUploadProgressState: UploadProgressState = {};

const updateFileState = (
  state: UploadProgressState,
  fileType: FileType,
  values: Partial<FileUploadState>
) => {
  return {
    ...state,
    [fileType]: {
      ...state[fileType],
      ...values,
    },
  };
};

export const buildStateFromFileMap = (
  fileMap: FileMap
): UploadProgressState => {
  const state: UploadProgressState = {};
  const newFileMap = _.omitBy(fileMap, (val) => _.isNil(val?.file));
  for (const [key, value] of Object.entries(newFileMap)) {
    state[key] = {
      name: value?.name,
      progress: 0,
      succcess: false,
      error: false,
      done: false,
    };
  }

  return state;
};

const reducer = (state: UploadProgressState, action: UploadProgressAction) => {
  switch (action.type) {
    case "init":
      return action.state;
    case "update":
      return updateFileState(state, action.fileType, {
        progress: action.progress,
      });
    case "done": {
      return updateFileState(state, action.fileType, {
        progress: 100,
        success: true,
        error: false,
        done: true,
      });
    }
    case "error": {
      return updateFileState(state, action.fileType, {
        progress: 100,
        success: false,
        error: true,
        done: true,
      });
    }
    default:
      throw new Error();
  }
};

interface ProcessFileUploadsReturn {
  error: boolean;
  files: string[];
}
const processFileUploads = async (
  dispatch: React.Dispatch<UploadProgressAction>,
  fileMap: FileMap
): Promise<ProcessFileUploadsReturn> => {
  const url = "/upload/product/insights";
  const headers = {
    "Content-Type": "multipart/form-data",
  };

  dispatch({ type: "init", state: buildStateFromFileMap(fileMap) });
  type FieldMapEntries = Array<[FileType, FileMapField]>;
  const fileMapEntries = Object.entries(fileMap) as FieldMapEntries;

  const files: string[] = [];
  let error = false;

  for (const [fileType, fields] of fileMapEntries) {
    // Preserve the file names even if the blob is missing so we can
    // safely pass the return of this function during an update.
    if (fields?.name && !fields?.file) {
      files.push(fields.name);
    }

    // If there's no blob, there's nothing to upload, just continue.
    if (!fields?.file) continue;
    const formData = new FormData();
    formData.append("file", fields.file);

    try {
      const onUploadProgress = (progressEvent: ProgressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        dispatch({ type: "update", fileType, progress: percentCompleted });
      };

      const uploadResponse = await axios.post(url, formData, {
        headers,
        onUploadProgress,
      });

      if (uploadResponse.status === 200) {
        files.push(fields.name);
        dispatch({ type: "done", fileType });
      } else {
        dispatch({ type: "error", fileType });
        error = true;
      }
    } catch (err) {
      dispatch({ type: "error", fileType });
      error = true;
    }
  }

  return {
    error,
    files,
  };
};

interface UseUploadProgressReturn {
  state: UploadProgressState;
  uploadFiles: (fileMap: FileMap) => Promise<ProcessFileUploadsReturn>;
}

type UseUploadProgressHook = () => UseUploadProgressReturn;

const useUploader: UseUploadProgressHook = () => {
  const [state, dispatch] = React.useReducer(
    reducer,
    initialUploadProgressState
  );

  return {
    state,
    uploadFiles: (fileMap: FileMap) => processFileUploads(dispatch, fileMap),
  };
};

export default useUploader;
